import type { TenantSummary } from '@rd/shared';
import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronsUpDown } from 'lucide-react';
import type { ReactElement } from 'react';

import { useAuth } from '../auth/AuthProvider.js';
import { Select } from '../components/Input.js';
import { apiPaginated } from '../lib/apiClient.js';

/**
 * Platform scope only (plan F5).
 *
 * A tenant-scope user has exactly one account and rendering a switcher for
 * them would suggest otherwise. The check is `user.scope`, which comes from
 * `/auth/me` — and, as ever, the API enforces it independently: a tenant user
 * who sends `X-Tenant-Ref` for someone else's account gets a 403, not data.
 */
export function TenantSwitcher(): ReactElement | null {
  const { user, tenant, selectTenant } = useAuth();

  const tenants = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => apiPaginated<TenantSummary>('/admin/tenants', { query: { pageSize: 100 } }),
    enabled: user?.scope === 'PLATFORM',
    // Phase 07 owns the tenant admin routes. Until they exist this 404s, and a
    // 404 here must degrade to "no switcher", not to an error screen.
    retry: false,
  });

  if (user?.scope !== 'PLATFORM') return null;

  const options = tenants.data?.data ?? (tenant ? [tenant] : []);

  return (
    /**
     * A bordered pill rather than a labelled field. In a 52px context bar the
     * word "Account" beside the control is the third thing competing for the
     * same 200px, and the control is self-describing once it carries the
     * account glyph — while `aria-label` keeps the *announced* name explicit,
     * which is the half that actually has to be unambiguous.
     *
     * The chevron is ours (`appearance-none`) because the native one differs
     * per platform and per OS theme: on a tenant with a dark surface the system
     * arrow can render nearly invisible, and nothing in `--brand-*` can reach
     * it to fix that.
     */
    <div className="relative flex items-center">
      <Building2
        aria-hidden="true"
        size={14}
        strokeWidth={2}
        className="pointer-events-none absolute left-[var(--space-3)] shrink-0 text-muted"
      />
      <Select
        value={tenant?.ref ?? ''}
        onChange={(event) => void selectTenant(event.target.value || null)}
        className="w-48 appearance-none pl-9 pr-9"
        aria-label="Switch account"
      >
        <option value="">All accounts</option>
        {options.map((t) => (
          <option key={t.ref} value={t.ref}>
            {t.name}
          </option>
        ))}
      </Select>
      <ChevronsUpDown
        aria-hidden="true"
        size={14}
        strokeWidth={2}
        className="pointer-events-none absolute right-[var(--space-3)] shrink-0 text-muted"
      />
    </div>
  );
}
